import { useEffect, useMemo, useState } from "react";
import logo from "../assets/LOGO - FOOTER.png";
import "./PublicBooking.css";

const API_BASE = "https://te-ayudo.es/back/api";

const DEFAULT_SERVICE_BY_SPECIALTY = {
  psicologia: "entrevista_inicial",
  logopeda: "entrevista_inicial",
  nutricion: "consulta_nutricional",
};

const SPECIALTY_INFO = {
  psicologia: {
    title: "Psicología",
    description:
      "Orientación, evaluación e intervención psicológica para infancia, adolescencia y familias.",
    icon: "🧠",
  },
  logopeda: {
    title: "Logopedia",
    description:
      "Valoración e intervención en lenguaje, habla, comunicación, voz y deglución.",
    icon: "💬",
  },
  nutricion: {
    title: "Nutrición",
    description:
      "Acompañamiento nutricional adaptado a las necesidades personales y familiares.",
    icon: "🥗",
  },
};

const EMPTY_PATIENT_FORM = {
  patient_id: null,
  nombre: "",
  fecha_nacimiento: "",
  sexo: "",
  tutor_nombre: "",
  tutor_telefono: "",
  tutor_email: "",
  notas: "",
  privacy_accepted: false,
};

const EMPTY_LOOKUP_FORM = {
  nombre: "",
};

function getToday() {
  const now = new Date();
  const offset = now.getTimezoneOffset();

  return new Date(now.getTime() - offset * 60000)
    .toISOString()
    .slice(0, 10);
}

function formatSelectedDate(date) {
  if (!date) return "";

  const parsedDate = new Date(`${date}T12:00:00`);

  return parsedDate.toLocaleDateString("es-ES", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
}

async function readJsonResponse(response) {
  const text = await response.text();

  if (!text) return {};

  try {
    return JSON.parse(text);
  } catch {
    console.error("Respuesta no JSON recibida:", text);
    throw new Error(
      "El servidor ha devuelto una respuesta inválida. Revisa el registro de errores de PHP."
    );
  }
}

export default function PublicBooking() {
  const [specialties, setSpecialties] = useState([]);
  const [specialty, setSpecialty] = useState("");

  const [modality, setModality] = useState("presencial");
  const [firstVisit, setFirstVisit] = useState(true);
  const [date, setDate] = useState(getToday());

  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);

  const [patientForm, setPatientForm] = useState(EMPTY_PATIENT_FORM);
  const [lookupForm, setLookupForm] = useState(EMPTY_LOOKUP_FORM);
  const [patientMatches, setPatientMatches] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [phoneLast3, setPhoneLast3] = useState("");
  const [existingPatientFound, setExistingPatientFound] = useState(false);
  const [showPatientForm, setShowPatientForm] = useState(false);

  const [loadingSpecialties, setLoadingSpecialties] = useState(true);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [searchingPatient, setSearchingPatient] = useState(false);
  const [verifyingPatient, setVerifyingPatient] = useState(false);
  const [creatingBooking, setCreatingBooking] = useState(false);

  const [hasSearched, setHasSearched] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [bookingResult, setBookingResult] = useState(null);

  useEffect(() => {
    const loadSpecialties = async () => {
      try {
        setLoadingSpecialties(true);
        setError("");

        const response = await fetch(`${API_BASE}/public.services.php`);
        const data = await readJsonResponse(response);

        if (!response.ok || !data.ok) {
          throw new Error(
            data.error || "No se pudieron cargar las especialidades"
          );
        }

        const allowedSpecialties = (data.items || []).filter((item) =>
          Object.prototype.hasOwnProperty.call(
            DEFAULT_SERVICE_BY_SPECIALTY,
            item.code
          )
        );

        setSpecialties(allowedSpecialties);

        if (allowedSpecialties.length > 0) {
          setSpecialty(allowedSpecialties[0].code);
        }
      } catch (err) {
        console.error(err);
        setError(
          err.message ||
            "No hemos podido cargar las opciones de reserva. Inténtalo de nuevo."
        );
      } finally {
        setLoadingSpecialties(false);
      }
    };

    loadSpecialties();
  }, []);

  const selectedSpecialty = useMemo(
    () => specialties.find((item) => item.code === specialty) || null,
    [specialties, specialty]
  );

  const selectedSpecialtyInfo =
    SPECIALTY_INFO[specialty] || {
      title: selectedSpecialty?.nombre || specialty,
      description: "",
      icon: "📅",
    };

  useEffect(() => {
    setSlots([]);
    setSelectedSlot(null);
    setShowPatientForm(false);
    setHasSearched(false);
    setError("");
    setFieldErrors({});
    setBookingResult(null);
  }, [specialty, modality, date]);

  const changeVisitType = (isFirstVisit) => {
    setFirstVisit(isFirstVisit);
    setExistingPatientFound(false);
    setLookupForm(EMPTY_LOOKUP_FORM);
    setPatientMatches([]);
    setSelectedCandidate(null);
    setPhoneLast3("");
    setPatientForm(EMPTY_PATIENT_FORM);
    setShowPatientForm(false);
    setError("");
    setFieldErrors({});
  };

  const updatePatientField = (field, value) => {
    setPatientForm((current) => ({
      ...current,
      [field]: value,
    }));

    setFieldErrors((current) => {
      const next = { ...current };
      delete next[field];
      delete next[`patient.${field}`];
      return next;
    });
  };

  const updateLookupField = (field, value) => {
    setLookupForm((current) => ({
      ...current,
      [field]: value,
    }));

    setPatientMatches([]);
    setSelectedCandidate(null);
    setPhoneLast3("");
    setExistingPatientFound(false);
    setPatientForm(EMPTY_PATIENT_FORM);
  };

  const searchExistingPatient = async () => {
    const name = lookupForm.nombre.trim();

    if (name.length < 3) {
      setError(
        "Escribe al menos 3 caracteres del nombre del paciente."
      );
      return;
    }

    try {
      setSearchingPatient(true);
      setError("");
      setFieldErrors({});
      setPatientMatches([]);
      setSelectedCandidate(null);
      setPhoneLast3("");
      setExistingPatientFound(false);

      const response = await fetch(
        `${API_BASE}/public/patients/search.php`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            nombre: name,
          }),
        }
      );

      const data = await readJsonResponse(response);

      if (!response.ok || !data.ok) {
        throw new Error(
          data.error ||
            "No hemos encontrado ninguna ficha con ese nombre."
        );
      }

      const matches = Array.isArray(data.matches)
        ? data.matches
        : [];

      if (matches.length === 0) {
        throw new Error(
          "No hemos encontrado ninguna ficha con ese nombre."
        );
      }

      setPatientMatches(matches);

      if (matches.length === 1) {
        setSelectedCandidate(matches[0]);
      }
    } catch (err) {
      console.error(err);
      setError(
        err.message ||
          "No hemos podido buscar la ficha. Inténtalo de nuevo."
      );
    } finally {
      setSearchingPatient(false);
    }
  };

  const verifyExistingPatient = async () => {
    if (!selectedCandidate?.candidate_token) {
      setError("Selecciona primero una de las fichas.");
      return;
    }

    if (!/^\d{3}$/.test(phoneLast3)) {
      setError(
        "Introduce exactamente los 3 últimos dígitos del teléfono."
      );
      return;
    }

    try {
      setVerifyingPatient(true);
      setError("");
      setFieldErrors({});

      const response = await fetch(
        `${API_BASE}/public/patients/verify.php`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            candidate_token:
              selectedCandidate.candidate_token,
            phone_last3: phoneLast3,
          }),
        }
      );

      const data = await readJsonResponse(response);

      if (!response.ok || !data.ok) {
        throw new Error(
          data.error ||
            "No hemos podido verificar la ficha."
        );
      }

      setPatientForm((current) => ({
        ...current,
        patient_id: null,
        nombre:
          data.display_name ||
          selectedCandidate.display_name,
      }));

      setExistingPatientFound(true);
      setError("");
    } catch (err) {
      console.error(err);
      setExistingPatientFound(false);
      setError(
        err.message ||
          "Los datos no coinciden. Revisa los tres dígitos."
      );
    } finally {
      setVerifyingPatient(false);
    }
  };

  const searchAvailability = async () => {
    const service = DEFAULT_SERVICE_BY_SPECIALTY[specialty];

    if (!specialty || !service || !date) {
      setError("Selecciona la especialidad, la modalidad y la fecha.");
      return;
    }

    try {
      setLoadingSlots(true);
      setHasSearched(true);
      setError("");
      setFieldErrors({});
      setSlots([]);
      setSelectedSlot(null);
      setShowPatientForm(false);

      const params = new URLSearchParams({
        specialty,
        service,
        date,
        modality,
        first_visit: firstVisit ? "1" : "0",
      });

      const response = await fetch(
        `${API_BASE}/public.availability.php?${params.toString()}`,
        {
          credentials: "include",
        }
      );

      const data = await readJsonResponse(response);

      if (!response.ok || !data.ok) {
        throw new Error(
          data.error ||
            data.message ||
            "No se pudo consultar la disponibilidad."
        );
      }

      setSlots(Array.isArray(data.slots) ? data.slots : []);
    } catch (err) {
      console.error(err);
      setError(
        err.message ||
          "No hemos podido consultar la disponibilidad. Inténtalo de nuevo."
      );
    } finally {
      setLoadingSlots(false);
    }
  };

  const validatePatientForm = () => {
    const errors = {};

    if (firstVisit) {
      if (!patientForm.nombre.trim()) {
        errors.nombre = "Escribe el nombre del paciente.";
      }

      if (!patientForm.tutor_nombre.trim()) {
        errors.tutor_nombre =
          "Escribe el nombre del padre, madre o tutor.";
      }

      if (!patientForm.tutor_telefono.trim()) {
        errors.tutor_telefono =
          "Escribe un teléfono de contacto.";
      }

      if (
        patientForm.tutor_email.trim() &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
          patientForm.tutor_email.trim()
        )
      ) {
        errors.tutor_email =
          "El correo electrónico no es válido.";
      }
    } else if (!existingPatientFound) {
      errors.patient_id =
        "Debes verificar primero la ficha del paciente.";
    }

    if (!patientForm.privacy_accepted) {
      errors.privacy_accepted =
        "Debes aceptar la política de privacidad.";
    }

    setFieldErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const createBooking = async (event) => {
    event.preventDefault();

    if (!selectedSlot) {
      setError(
        "Selecciona primero uno de los horarios disponibles."
      );
      return;
    }

    if (!firstVisit && !existingPatientFound) {
      setError(
        "Localiza primero la ficha del paciente."
      );
      return;
    }

    if (!validatePatientForm()) {
      setError("Revisa los datos indicados.");
      return;
    }

    const serviceCode =
      DEFAULT_SERVICE_BY_SPECIALTY[specialty];

    const payload = {
      patient_id: patientForm.patient_id || null,
      patient: {
        nombre: patientForm.nombre.trim(),
        fecha_nacimiento:
          patientForm.fecha_nacimiento || null,
        sexo: patientForm.sexo || null,
        tutor_nombre: patientForm.tutor_nombre.trim(),
        tutor_telefono:
          patientForm.tutor_telefono.trim(),
        tutor_email:
          patientForm.tutor_email.trim() || null,
        notas: patientForm.notas.trim() || null,
      },
      specialty,
      service_code: serviceCode,
      modality,
      first_visit: firstVisit,
      professional_id: Number(
        selectedSlot.professional_id
      ),
      start_datetime: selectedSlot.start,
      end_datetime: selectedSlot.end,
    };

    try {
      setCreatingBooking(true);
      setError("");
      setFieldErrors({});

      const response = await fetch(
        `${API_BASE}/public/bookings/create.php`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await readJsonResponse(response);

      if (!response.ok || !data.ok) {
        if (
          data.fields &&
          typeof data.fields === "object"
        ) {
          setFieldErrors(data.fields);
        }

        if (
          response.status === 409 ||
          data.code === "SLOT_UNAVAILABLE"
        ) {
          setSelectedSlot(null);
          setShowPatientForm(false);
          await searchAvailability();

          throw new Error(
            data.error ||
              "Ese horario acaba de dejar de estar disponible. Hemos actualizado las opciones."
          );
        }

        throw new Error(
          data.error ||
            data.message ||
            "No se ha podido completar la reserva."
        );
      }

      setBookingResult(data.booking || {});
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } catch (err) {
      console.error(err);
      setError(
        err.message ||
          "No se ha podido completar la reserva. Inténtalo de nuevo."
      );
    } finally {
      setCreatingBooking(false);
    }
  };

  const resetBooking = () => {
    setBookingResult(null);
    setPatientForm(EMPTY_PATIENT_FORM);
    setLookupForm(EMPTY_LOOKUP_FORM);
    setPatientMatches([]);
    setSelectedCandidate(null);
    setPhoneLast3("");
    setExistingPatientFound(false);
    setSlots([]);
    setSelectedSlot(null);
    setShowPatientForm(false);
    setHasSearched(false);
    setError("");
    setFieldErrors({});
    setDate(getToday());
  };

  if (bookingResult) {
    return (
      <main className="public-booking-page">
        <section className="public-booking-card">
          <header className="public-booking-header">
            <img
              src={logo}
              alt="Te Ayudo"
              className="public-booking-logo"
            />

            <span className="public-booking-eyebrow">
              Reserva confirmada
            </span>

            <h1>Tu cita ha quedado reservada</h1>

            <p>
              Hemos registrado correctamente la cita.
            </p>
          </header>

          <section className="booking-confirmation">
            <div className="booking-confirmation-icon">
              ✓
            </div>

            <h2>Reserva completada</h2>

            <div className="booking-summary">
              <div>
                <span>Paciente</span>
                <strong>{patientForm.nombre}</strong>
              </div>

              <div>
                <span>Especialidad</span>
                <strong>
                  {selectedSpecialtyInfo.title}
                </strong>
              </div>

              <div>
                <span>Fecha</span>
                <strong>
                  {formatSelectedDate(
                    bookingResult.start_datetime?.slice(
                      0,
                      10
                    ) || date
                  )}
                </strong>
              </div>

              <div>
                <span>Horario</span>
                <strong>
                  {selectedSlot?.label ||
                    bookingResult.start_datetime?.slice(
                      11,
                      16
                    )}
                </strong>
              </div>

              {bookingResult.id && (
                <div>
                  <span>Referencia</span>
                  <strong>
                    #{bookingResult.id}
                  </strong>
                </div>
              )}
            </div>

            <button
              type="button"
              className="booking-search-button"
              onClick={resetBooking}
            >
              Realizar otra reserva
            </button>
          </section>
        </section>
      </main>
    );
  }

  return (
    <main className="public-booking-page">
      <section className="public-booking-card">
        <header className="public-booking-header">
          <img
            src={logo}
            alt="Te Ayudo"
            className="public-booking-logo"
          />

          <span className="public-booking-eyebrow">
            Reserva online
          </span>

          <h1>Reserva tu cita</h1>

          <p>
            Indícanos qué necesitas y te mostraremos
            las opciones disponibles más próximas.
          </p>
        </header>

        {error && (
          <div
            className="booking-message error"
            role="alert"
          >
            {error}
          </div>
        )}

        <section className="booking-section">
          <div className="booking-section-title">
            <span>1</span>

            <div>
              <h2>¿Es tu primera visita?</h2>
              <p>
                Esto nos ayuda a organizar mejor tu
                atención.
              </p>
            </div>
          </div>

          <div className="booking-options">
            <button
              type="button"
              className={
                firstVisit ? "selected" : ""
              }
              onClick={() => changeVisitType(true)}
            >
              <strong>
                Sí, es mi primera visita
              </strong>
              <small>
                Quiero agendar una sesión
              </small>
            </button>

            <button
              type="button"
              className={
                !firstVisit ? "selected" : ""
              }
              onClick={() => changeVisitType(false)}
            >
              <strong>
                No, ya soy paciente
              </strong>
              <small>
                Buscar mi ficha del centro
              </small>
            </button>
          </div>
        </section>

        {!firstVisit && (
          <section className="booking-section booking-lookup">
            <div className="booking-section-title">
              <span>2</span>

              <div>
                <h2>Busca tu ficha</h2>
                <p>
                  Escribe el nombre del paciente. Los apellidos
                  aparecerán únicamente con sus iniciales.
                </p>
              </div>
            </div>

            <div className="booking-form-grid booking-lookup-grid">
              <label className="booking-field booking-field-full">
                <span>Nombre del paciente *</span>

                <input
                  type="text"
                  value={lookupForm.nombre}
                  onChange={(event) =>
                    updateLookupField(
                      "nombre",
                      event.target.value
                    )
                  }
                  placeholder="Ej.: Paula López García"
                  autoComplete="off"
                />
              </label>
            </div>

            <button
              type="button"
              className="booking-search-button"
              onClick={searchExistingPatient}
              disabled={searchingPatient}
            >
              {searchingPatient
                ? "Buscando ficha…"
                : "Buscar ficha"}
            </button>

            {patientMatches.length > 0 &&
              !existingPatientFound && (
                <div className="booking-patient-matches">
                  <h3>
                    Selecciona la ficha que corresponda
                  </h3>

                  <div className="booking-patient-match-list">
                    {patientMatches.map((match, index) => {
                      const selected =
                        selectedCandidate?.candidate_token ===
                        match.candidate_token;

                      return (
                        <button
                          key={match.candidate_token}
                          type="button"
                          className={
                            selected
                              ? "booking-patient-match selected"
                              : "booking-patient-match"
                          }
                          onClick={() => {
                            setSelectedCandidate(match);
                            setPhoneLast3("");
                            setError("");
                          }}
                        >
                          <span>
                            {match.display_name}
                          </span>

                          <small>
                            Ficha {index + 1}
                          </small>
                        </button>
                      );
                    })}
                  </div>

                  {selectedCandidate && (
                    <div className="booking-verification-box">
                      <div>
                        <h3>Confirma tu identidad</h3>
                        <p>
                          Introduce los 3 últimos dígitos
                          del teléfono guardado en la ficha.
                        </p>
                      </div>

                      <div className="booking-verification-row">
                        <input
                          type="text"
                          inputMode="numeric"
                          maxLength="3"
                          value={phoneLast3}
                          onChange={(event) =>
                            setPhoneLast3(
                              event.target.value
                                .replace(/\D/g, "")
                                .slice(0, 3)
                            )
                          }
                          placeholder="000"
                          aria-label="Tres últimos dígitos del teléfono"
                        />

                        <button
                          type="button"
                          className="booking-continue-button"
                          onClick={verifyExistingPatient}
                          disabled={verifyingPatient}
                        >
                          {verifyingPatient
                            ? "Verificando…"
                            : "Verificar ficha"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

            {existingPatientFound && (
              <div className="booking-message success">
                Ficha verificada correctamente:{" "}
                <strong>{patientForm.nombre}</strong>
              </div>
            )}
          </section>
        )}

        {(firstVisit || existingPatientFound) && (
          <>
            <section className="booking-section">
              <div className="booking-section-title">
                <span>{firstVisit ? "2" : "3"}</span>

                <div>
                  <h2>
                    Selecciona la especialidad
                  </h2>
                </div>
              </div>

              {loadingSpecialties ? (
                <div className="booking-loading">
                  Cargando especialidades…
                </div>
              ) : (
                <div className="specialty-grid">
                  {specialties.map((item) => {
                    const info =
                      SPECIALTY_INFO[item.code] || {
                        title: item.nombre,
                        description: "",
                        icon: "📅",
                      };

                    return (
                      <button
                        key={item.id}
                        type="button"
                        className={
                          specialty === item.code
                            ? "specialty-card selected"
                            : "specialty-card"
                        }
                        onClick={() =>
                          setSpecialty(item.code)
                        }
                      >
                        <span className="specialty-icon">
                          {info.icon}
                        </span>

                        <div>
                          <strong>
                            {info.title}
                          </strong>
                          <p>
                            {info.description}
                          </p>
                        </div>

                        <span className="specialty-check">
                          {specialty === item.code
                            ? "✓"
                            : ""}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </section>

            <section className="booking-section">
              <div className="booking-section-title">
                <span>{firstVisit ? "3" : "4"}</span>

                <div>
                  <h2>
                    Modalidad de la cita
                  </h2>
                  <p>
                    Elige cómo prefieres que se
                    realice.
                  </p>
                </div>
              </div>

              <div className="booking-options">
                <button
                  type="button"
                  className={
                    modality === "presencial"
                      ? "selected"
                      : ""
                  }
                  onClick={() =>
                    setModality("presencial")
                  }
                >
                  <strong>Presencial</strong>
                  <small>
                    En nuestro centro de Jerez
                  </small>
                </button>

                <button
                  type="button"
                  className={
                    modality === "online"
                      ? "selected"
                      : ""
                  }
                  onClick={() =>
                    setModality("online")
                  }
                >
                  <strong>Online</strong>
                  <small>
                    Desde casa mediante
                    videollamada
                  </small>
                </button>
              </div>
            </section>

            <section className="booking-section">
              <div className="booking-section-title">
                <span>{firstVisit ? "4" : "5"}</span>

                <div>
                  <h2>
                    ¿Qué día te viene mejor?
                  </h2>
                  <p>
                    Te mostraremos una selección
                    de opciones disponibles.
                  </p>
                </div>
              </div>

              <div className="booking-date-wrapper">
                <input
                  type="date"
                  min={getToday()}
                  value={date}
                  onChange={(event) =>
                    setDate(event.target.value)
                  }
                />

                <span className="booking-date-text">
                  {formatSelectedDate(date)}
                </span>
              </div>
            </section>

            <div className="booking-summary">
              <div>
                <span>Especialidad</span>
                <strong>
                  {selectedSpecialtyInfo.title}
                </strong>
              </div>

              <div>
                <span>Modalidad</span>
                <strong>
                  {modality === "presencial"
                    ? "Presencial"
                    : "Online"}
                </strong>
              </div>

              <div>
                <span>Tipo</span>
                <strong>
                  {firstVisit
                    ? "Primera visita"
                    : "Paciente del centro"}
                </strong>
              </div>
            </div>

            <button
              type="button"
              className="booking-search-button"
              onClick={searchAvailability}
              disabled={
                loadingSlots ||
                loadingSpecialties ||
                !specialty ||
                !date
              }
            >
              {loadingSlots
                ? "Buscando las mejores opciones…"
                : "Ver próximas opciones"}
            </button>

            {!loadingSlots &&
              hasSearched &&
              slots.length > 0 && (
                <section className="booking-slots">
                  <div className="booking-slot-list">
                    {slots.map((slot, index) => {
                      const isSelected =
                        selectedSlot?.start ===
                          slot.start &&
                        selectedSlot?.professional_id ===
                          slot.professional_id;

                      return (
                        <button
                          key={`${slot.professional_id}-${slot.start}`}
                          type="button"
                          className={
                            isSelected
                              ? "booking-slot selected"
                              : "booking-slot"
                          }
                          onClick={() => {
                            setSelectedSlot(slot);
                            setShowPatientForm(false);
                            setError("");
                          }}
                        >
                          <div>
                            <small>
                              {index === 0
                                ? "Primera opción"
                                : `Opción ${
                                    index + 1
                                  }`}
                            </small>

                            <strong>
                              {slot.label}
                            </strong>
                          </div>

                          <span>
                            {isSelected
                              ? "✓"
                              : "›"}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </section>
              )}

            {selectedSlot &&
              !showPatientForm && (
                <section className="booking-selected">
                  <div>
                    <span>
                      Horario seleccionado
                    </span>

                    <strong>
                      {formatSelectedDate(date)} ·{" "}
                      {selectedSlot.label}
                    </strong>
                  </div>

                  <button
                    type="button"
                    className="booking-continue-button"
                    onClick={() =>
                      setShowPatientForm(true)
                    }
                  >
                    Continuar con la reserva
                  </button>
                </section>
              )}

            {selectedSlot &&
              showPatientForm && (
                <section className="booking-section booking-patient-form">
                  <div className="booking-section-title">
                    <span>
                      {firstVisit ? "5" : "6"}
                    </span>

                    <div>
                      <h2>
                        Datos para la reserva
                      </h2>
                      <p>
                        Confirma los datos de
                        contacto.
                      </p>
                    </div>
                  </div>

                  <form
                    onSubmit={createBooking}
                    noValidate
                  >
                    {firstVisit ? (
                    <div className="booking-form-grid">
                      <label className="booking-field">
                        <span>
                          Nombre del paciente *
                        </span>
                        <input
                          type="text"
                          value={patientForm.nombre}
                          disabled={
                            !firstVisit &&
                            existingPatientFound
                          }
                          onChange={(event) =>
                            updatePatientField(
                              "nombre",
                              event.target.value
                            )
                          }
                        />
                      </label>

                      <label className="booking-field">
                        <span>
                          Fecha de nacimiento
                        </span>
                        <input
                          type="date"
                          max={getToday()}
                          value={
                            patientForm.fecha_nacimiento
                          }
                          disabled={
                            !firstVisit &&
                            existingPatientFound
                          }
                          onChange={(event) =>
                            updatePatientField(
                              "fecha_nacimiento",
                              event.target.value
                            )
                          }
                        />
                      </label>

                      <label className="booking-field">
                        <span>Sexo</span>
                        <select
                          value={patientForm.sexo}
                          disabled={
                            !firstVisit &&
                            existingPatientFound
                          }
                          onChange={(event) =>
                            updatePatientField(
                              "sexo",
                              event.target.value
                            )
                          }
                        >
                          <option value="">
                            Prefiero no indicarlo
                          </option>
                          <option value="F">
                            Femenino
                          </option>
                          <option value="M">
                            Masculino
                          </option>
                          <option value="O">
                            Otro
                          </option>
                        </select>
                      </label>

                      <label className="booking-field">
                        <span>
                          Nombre del padre, madre o
                          tutor *
                        </span>
                        <input
                          type="text"
                          value={
                            patientForm.tutor_nombre
                          }
                          onChange={(event) =>
                            updatePatientField(
                              "tutor_nombre",
                              event.target.value
                            )
                          }
                        />
                      </label>

                      <label className="booking-field">
                        <span>
                          Teléfono de contacto *
                        </span>
                        <input
                          type="tel"
                          value={
                            patientForm.tutor_telefono
                          }
                          onChange={(event) =>
                            updatePatientField(
                              "tutor_telefono",
                              event.target.value
                            )
                          }
                        />
                      </label>

                      <label className="booking-field">
                        <span>
                          Correo electrónico
                        </span>
                        <input
                          type="email"
                          value={
                            patientForm.tutor_email
                          }
                          onChange={(event) =>
                            updatePatientField(
                              "tutor_email",
                              event.target.value
                            )
                          }
                        />
                      </label>

                      <label className="booking-field booking-field-full">
                        <span>
                          Motivo de consulta
                        </span>
                        <textarea
                          rows="4"
                          value={patientForm.notas}
                          onChange={(event) =>
                            updatePatientField(
                              "notas",
                              event.target.value
                            )
                          }
                        />
                      </label>
                    </div>

                    ) : (
                      <div className="booking-existing-patient-summary">
                        <span>Paciente verificado</span>
                        <strong>{patientForm.nombre}</strong>
                        <p>
                          La cita se guardará en la ficha existente.
                          No se mostrarán ni se solicitarán otros datos personales.
                        </p>

                        <label className="booking-field booking-field-full">
                          <span>Motivo de consulta</span>
                          <textarea
                            rows="4"
                            value={patientForm.notas}
                            onChange={(event) =>
                              updatePatientField(
                                "notas",
                                event.target.value
                              )
                            }
                            placeholder="Opcional"
                          />
                        </label>
                      </div>
                    )}
                    <label className="booking-privacy-check">
                      <input
                        type="checkbox"
                        checked={
                          patientForm.privacy_accepted
                        }
                        onChange={(event) =>
                          updatePatientField(
                            "privacy_accepted",
                            event.target.checked
                          )
                        }
                      />

                      <span>
                        He leído y acepto la
                        política de privacidad.
                      </span>
                    </label>

                    <div className="booking-form-actions">
                      <button
                        type="button"
                        className="booking-secondary-button"
                        onClick={() =>
                          setShowPatientForm(false)
                        }
                      >
                        Volver
                      </button>

                      <button
                        type="submit"
                        className="booking-continue-button"
                        disabled={creatingBooking}
                      >
                        {creatingBooking
                          ? "Confirmando reserva…"
                          : "Confirmar reserva"}
                      </button>
                    </div>
                  </form>
                </section>
              )}
          </>
        )}
      </section>
    </main>
  );
}
